/* eslint-disable max-lines */
import * as axios from 'axios';
import { DUMMY_ACCESS_TOKEN } from '../../constants/AppConstants';
import { AppSettings } from '../../AppSettings';
import { getEnv, getEnvForLogin } from '../helper/HelperFunctions';
import { API_ROUTE } from './ApiRoute';
import AxiosInstance from './AxiosInstance';
import { HttpContentType } from './HttpContentType';
import { storage, STORAGE_CONSTANTS } from './LocalStorage';
import { IMultipartFormData } from './MultipartFormData.data';
import { QueryPath } from './QueryPath.data';
import { ServerType, ServiceType } from './ServiceType.data';

// Global token refresh state shared across all instances
class TokenRefreshManager {
  private static dcaBackendRefreshing = false;
  private static dcaBackendSubscribers: any[] = [];
  private static cwbRefreshing = false;
  private static cwbSubscribers: any[] = [];
  private static readonly REFRESH_TIMEOUT = 30000; // 30 seconds timeout

  static isDcaBackendRefreshing(): boolean {
    return this.dcaBackendRefreshing;
  }

  static isCwbRefreshing(): boolean {
    return this.cwbRefreshing;
  }

  static setDcaBackendRefreshing(isRefreshing: boolean): void {
    this.dcaBackendRefreshing = isRefreshing;
    if (isRefreshing) {
      // Set timeout to prevent infinite hanging
      setTimeout(() => {
        if (this.dcaBackendRefreshing) {
          console.error('DCA Backend token refresh timeout');
          this.dcaBackendRefreshing = false;
          this.notifyDcaBackendSubscribers(new Error('Token refresh timeout'));
        }
      }, this.REFRESH_TIMEOUT);
    }
  }

  static setCwbRefreshing(isRefreshing: boolean): void {
    this.cwbRefreshing = isRefreshing;
    if (isRefreshing) {
      // Set timeout to prevent infinite hanging
      setTimeout(() => {
        if (this.cwbRefreshing) {
          console.error('CWB token refresh timeout');
          this.cwbRefreshing = false;
          this.notifyCwbSubscribers(new Error('Token refresh timeout'));
        }
      }, this.REFRESH_TIMEOUT);
    }
  }

  static subscribeDcaBackend(callback: any): void {
    this.dcaBackendSubscribers.push(callback);
  }

  static subscribeCwb(callback: any): void {
    this.cwbSubscribers.push(callback);
  }

  static notifyDcaBackendSubscribers(error: Error | null): void {
    this.dcaBackendSubscribers.forEach(cb => cb(error));
    this.dcaBackendSubscribers = [];
  }

  static notifyCwbSubscribers(error: Error | null): void {
    this.cwbSubscribers.forEach(cb => cb(error));
    this.cwbSubscribers = [];
  }
}

/// <summary>
/// ApiServiceMock cannot inherit ApiService, because that's mocked and that would create an infinite loop, that's why we need ApiServiceBase.
/// </summary>
export default abstract class ApiServiceBase {
  protected readonly serviceType: ServiceType;
  protected readonly serverType: ServerType;

  constructor(serviceType: ServiceType, serverType: ServerType) {
    this.serviceType = serviceType;
    this.serverType = serverType;
  }

  public abstract get<T = void>(path: QueryPath): Promise<T> | T;

  public abstract post<T = void>(path: QueryPath, body: any): Promise<T> | T;

  public abstract put<T = void>(path: QueryPath, body: any): Promise<T> | T;

  public abstract patch<T = void>(path: QueryPath, body: any): Promise<T> | T;

  public abstract delete<T = void>(path: QueryPath): Promise<T>;

  public abstract postMultipart<T = void>(
    path: QueryPath,
    data: IMultipartFormData[],
  ): Promise<T> | T;

  public abstract putMultipart<T = void>(
    path: QueryPath,
    data: IMultipartFormData[],
  ): Promise<T> | T;

  public abstract patchMultipart<T = void>(
    path: QueryPath,
    data: IMultipartFormData[],
  ): Promise<T> | T;

  /* tslint:disable:cyclomatic-complexity */
  public processError(error: any) {
    const errorCode = error.response ? error.response.status || 500 : 500;
    switch (errorCode) {
      case 404:
        return new Error('The request is not found');
      case 500:
        return new Error('Internal server error');
      case 401:
        // eslint-disable-next-line @typescript-eslint/no-unsafe-return
        return {
          ...error.response.data,
        };
      case 400:
      case 422: {
        if (error.response.data.errors) {
          const err = error.response.data.errors;

          if (err instanceof Array) {
            const errArr = err;

            if (errArr.length > 0 && errArr[0]) {
              if (errArr[0].message) {
                return new Error(errArr[0].message.toString());
              } else if (errArr[0].Message) {
                return new Error(errArr[0].Message.toString());
              } else {
                return new Error(errArr[0].toString());
              }
            }
          } else if (err.message) {
            return new Error(err.message.toString());
          } else if (err.Message) {
            return new Error(err.Message.toString());
          } else {
            return new Error(err.toString());
          }
        } else if (error.response.data) {
          if (error.response.data.msg) {
            return new Error(error.response.data.msg.toString());
          } else {
            return new Error(error.response.data.toString());
          }
        }

        return new Error('Internal server error');
      }
      default:
        // eslint-disable-next-line @typescript-eslint/no-unsafe-return
        return error;
    }
  }

  /* tslint:enable */
  protected getConfig(contentType: HttpContentType): axios.AxiosRequestConfig {
    let headers;
    if (this.serverType == ServerType.dcp) {
      const userId = storage.getItem(STORAGE_CONSTANTS.userId);
      const userType = storage.getItem(STORAGE_CONSTANTS.userType);
      headers = {
        'Content-Type': contentType.toString(),
        access_token: storage.getItem(STORAGE_CONSTANTS.accessToken) || DUMMY_ACCESS_TOKEN,
        userid: `${userId}`,
        user_type: `${userType}`,
      };
    } else if (this.serverType === ServerType.dcaBackend) {
      const userType = storage.getItem(STORAGE_CONSTANTS.userType);
      headers = {
        'Content-Type': contentType.toString(),
        'Authorization': `Bearer ${
          storage.getItem(STORAGE_CONSTANTS.accessToken) || DUMMY_ACCESS_TOKEN
        }`,
        user_type: `${userType}`,
      };
    } else if (this.serverType == ServerType.tangra) {
      const cwbIdToken = storage.getItem(STORAGE_CONSTANTS.cwbIdToken);
      const userId = storage.getItem(STORAGE_CONSTANTS.userId);
      const userType = storage.getItem(STORAGE_CONSTANTS.userType);
      headers = this.serviceType.startsWith('user')
        ? {
            'Content-Type': contentType.toString(),
            id_token: `Bearer ${cwbIdToken}`,
            access_token: storage.getItem(STORAGE_CONSTANTS.accessToken) || DUMMY_ACCESS_TOKEN,
            cwb_flag: `${Boolean(cwbIdToken)}`,
            userid: `${userId}`,
            user_type: `${userType}`,
          }
        : {
            'Content-Type': contentType.toString(),
            access_token: storage.getItem(STORAGE_CONSTANTS.accessToken) || DUMMY_ACCESS_TOKEN,
            userid: `${userId}`,
            user_type: `${userType}`,
          };
    } else if (this.serverType == ServerType.local) {
      const userId = storage.getItem(STORAGE_CONSTANTS.userId);
      const userType = storage.getItem(STORAGE_CONSTANTS.userType);
      headers = {
        'Content-Type': contentType.toString(),
        access_token: storage.getItem(STORAGE_CONSTANTS.accessToken) || DUMMY_ACCESS_TOKEN,
        userid: `${userId}`,
        user_type: `${userType}`,
      };
    } else if (this.serverType == ServerType.sap) {
      const userId = storage.getItem(STORAGE_CONSTANTS.userId);
      const userType = storage.getItem(STORAGE_CONSTANTS.userType);
      headers = {
        'Content-Type': contentType.toString(),
        // 'Content-Type': 'application/json',
        // access_token: storage.getItem(STORAGE_CONSTANTS.accessToken) || DUMMY_ACCESS_TOKEN,
        // userid: `${userId}`,
        // user_type: `${userType}`,
      };
    } else if (this.serverType == ServerType.faultInjectionStatus) {
      // Fault Injection Status
      headers = {
        'Content-Type': contentType.toString(),
      };
    } else {
      headers = this.serviceType.startsWith('user')
        ? {
            'Content-Type': contentType.toString(),
            id_token: `Bearer ${storage.getItem(STORAGE_CONSTANTS.cwbIdToken)}`,
          }
        : this.serviceType.startsWith('tco') ||
            this.serviceType.startsWith('csp') ||
            this.serviceType.startsWith('oma')
          ? {
              'Content-Type': contentType.toString(),
              idtoken: `Bearer ${storage.getItem(STORAGE_CONSTANTS.cwbIdToken)}`,
            }
          : {
              'Content-Type': contentType.toString(),
              idtoken: `Basic ${storage.getItem(STORAGE_CONSTANTS.authTokenBasic)}`,
            };
    }

    return { headers };
  }

  protected isAuthTokenRequired(path: string): boolean {
    return path.includes('/api');
  }

  protected getAxiosInstance(): axios.AxiosInstance {
    const instance = AxiosInstance.create();
    // const { baseUrl } = AppSettings;

    const subscribeTokenRefresh = (callBack: any) => {
      // This function is no longer used - replaced by TokenRefreshManager
      // Keeping for backward compatibility but it doesn't do anything
    };

    const onTokenRefreshed = (error: Error | null) => {
      // This function is no longer used - replaced by TokenRefreshManager
      // Keeping for backward compatibility but it doesn't do anything
    };

    const forceLogout = () => {
      TokenRefreshManager.setDcaBackendRefreshing(false);
      TokenRefreshManager.setCwbRefreshing(false);
      storage.clearAll();
      window.location.href = `/#/login`;
    };

    const clearCwbTokens = () => {
      storage.removeItem(STORAGE_CONSTANTS.cwbIdToken);
      storage.removeItem(STORAGE_CONSTANTS.cwbAccessToken);
      storage.removeItem(STORAGE_CONSTANTS.cwbRefreshToken);
      storage.removeItem(STORAGE_CONSTANTS.cwbUserDetails);
    };

    instance.interceptors.response.use(
      (response: any) => {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-return
        return response;
      },
      (error: any) => {
        if (!error.response) {
          return Promise.reject(error);
        }

        if (
          error.response.status !== 403 &&
          error.response.status !== 401 &&
          error.response.status !== 400
        ) {
          return Promise.reject(error);
        }

        if (error.config.url.includes('login')) {
          return Promise.reject(error);
        }

        // If refreshToken of CWB expires, cancel the asset authorization of CWB
        if (error.config.url.includes('cwb-refresh-token') && error.response.status === 400) {
          TokenRefreshManager.notifyCwbSubscribers(new Error('Unable to refresh access token'));
          clearCwbTokens();
          return Promise.reject(error);
        }

        // If refreshToken API, force logout
        if (error.config.url.includes('refresh_token') && error.response.status === 400) {
          TokenRefreshManager.notifyDcaBackendSubscribers(new Error('Unable to refresh access token'));
          forceLogout();
          return Promise.reject(error);
        }

        // CWB id Token could be expired if 1. or 2.
        // 1. CWB APIs fails with 401
        // 2. Tangra Projects listing API fails with 403 and return failedAsset as CWB
        const isCwbTokenExpired =
          (this.serverType === ServerType.cwb && error.response.status === 401) ||
          (this.serverType === ServerType.tangra &&
            this.serviceType.startsWith('user') &&
            error.response.data?.failedAsset === ServerType.cwb &&
            error.response.status === 403);

        const cwbRefreshToken = storage.getItem(STORAGE_CONSTANTS.cwbRefreshToken);

        // Add explicit check when token is expired but no refresh token exists
        if (isCwbTokenExpired && !cwbRefreshToken) {
          console.error('CWB token expired and no refresh token available');
          clearCwbTokens(); // Clear any remaining tokens
          return Promise.reject(error);
        }


        if (isCwbTokenExpired && cwbRefreshToken) {
          if (!TokenRefreshManager.isCwbRefreshing()) {
            TokenRefreshManager.setCwbRefreshing(true);

            return instance
              .get(
                `${AppSettings.tangraURL}/${API_ROUTE.CWB.REFRESH_TOKEN}?refresh_token=${cwbRefreshToken}`,
                {
                  headers: {
                    access_token:
                      storage.getItem(STORAGE_CONSTANTS.accessToken) || DUMMY_ACCESS_TOKEN,
                    userid: `${storage.getItem(STORAGE_CONSTANTS.userId)}`,
                    user_type: `${storage.getItem(STORAGE_CONSTANTS.userType)}`,
                  },
                },
              )
              .then((response) => {
                const { access_token, id_token } = response.data;
                TokenRefreshManager.setCwbRefreshing(false);
                error.config.headers.access_token = access_token;
                error.config.headers.Authorization = `Bearer ${access_token}`;
                storage.setItem(STORAGE_CONSTANTS.cwbAccessToken, access_token);
                storage.setItem(STORAGE_CONSTANTS.cwbIdToken, id_token);
                if (this.serverType === ServerType.cwb) {
                  error.config.headers.idtoken = id_token;
                }
                if (this.serverType === ServerType.tangra) {
                  error.config.headers.id_token = id_token;
                  error.config.headers.cwb_flag = 'true';
                }
                TokenRefreshManager.notifyCwbSubscribers(null);
                return instance(error.config);
              })
              .catch((refreshError) => {
                TokenRefreshManager.setCwbRefreshing(false);
                TokenRefreshManager.notifyCwbSubscribers(refreshError);
                clearCwbTokens();
                return Promise.reject(refreshError);
              });
          } else {
            const initTokenSubscriber = new Promise((resolve, reject) => {
              TokenRefreshManager.subscribeCwb((errRefreshing: any) => {
                if (errRefreshing) {
                  return reject(errRefreshing);
                }
                const accessToken = storage.getItem(STORAGE_CONSTANTS.accessToken);
                error.config.headers.access_token = accessToken;
                error.config.headers.Authorization = `Bearer ${accessToken}`; // Add Bearer prefix
                const cwbIdToken = storage.getItem(STORAGE_CONSTANTS.cwbIdToken);
                if (this.serverType === ServerType.cwb) {
                  error.config.headers.idtoken = cwbIdToken;
                }
                if (this.serverType === ServerType.tangra) {
                  error.config.headers.id_token = cwbIdToken;
                  error.config.headers.cwb_flag = 'true';
                }
                return resolve(instance(error.config));
              });
            });
            return initTokenSubscriber;
          }
        }

        // DCA Backend Access Token could be expired when any Backend API fails with 403.
        // Only refresh on auth failures (error === 'Unauthorized'), not permission denials.
        const isDcaAuthFailure =
          error.response.status === 403 &&
          this.serverType === ServerType.dcaBackend &&
          error.response.data?.error === 'Unauthorized';
        if (isDcaAuthFailure) {
          if (!TokenRefreshManager.isDcaBackendRefreshing()) {
            const currentRefreshToken = storage.getItem(STORAGE_CONSTANTS.refreshToken);
  
            // Check if refresh token exists
            if (!currentRefreshToken) {
              // No refresh token exists, force logout
              console.error('Access token expired and no refresh token available');
              forceLogout();
              return Promise.reject(error);
            }
            TokenRefreshManager.setDcaBackendRefreshing(true);
            const env = getEnv();
            const userType = storage.getItem(STORAGE_CONSTANTS.userType);
            return instance
              .get(
                `${AppSettings.DCABackendServer}/${
                  API_ROUTE.REFRESH_TOKEN
                }?refresh_token=${currentRefreshToken}&user_type=${userType}&env=${getEnvForLogin(
                  env,
                )}`,
                {
                  headers: {
                    Authorization:
                      storage.getItem(STORAGE_CONSTANTS.accessToken) || DUMMY_ACCESS_TOKEN,
                    access_token:
                      storage.getItem(STORAGE_CONSTANTS.accessToken) || DUMMY_ACCESS_TOKEN,
                    userid: `${storage.getItem(STORAGE_CONSTANTS.userId)}`,
                    user_type: `${userType}`,
                  },
                },
              )
              .then((response) => {
                const { access_token } = response.data;
                TokenRefreshManager.setDcaBackendRefreshing(false);
                storage.setItem(STORAGE_CONSTANTS.accessToken, access_token);
                error.config.headers.access_token = access_token;
                error.config.headers.Authorization = `Bearer ${access_token}`;
                TokenRefreshManager.notifyDcaBackendSubscribers(null);
                return instance(error.config);
              })
              .catch((refreshError) => {
                TokenRefreshManager.setDcaBackendRefreshing(false);
                TokenRefreshManager.notifyDcaBackendSubscribers(refreshError);
                forceLogout();
                return Promise.reject(refreshError);
              });
          } else {
            const initTokenSubscriber = new Promise((resolve, reject) => {
              TokenRefreshManager.subscribeDcaBackend((errRefreshing: any) => {
                if (errRefreshing) {
                  return reject(errRefreshing);
                }
                const accessToken = storage.getItem(STORAGE_CONSTANTS.accessToken);
                error.config.headers.access_token = accessToken;
                error.config.headers.Authorization = `Bearer ${accessToken}`;
                return resolve(instance(error.config));
              });
            });
            return initTokenSubscriber;
          }
        }
        return Promise.reject(error);
      },
    );
    return instance;
  }

  // Generates url: {AppSettings.service.baseUrl}/{this.serviceType}/{routeParam1}/{routeParam2}/.../{routeParamN}?{queryParam1key}={queryParam1val}&{queryParam2key}={queryParam2val}...
  // Query params with null, undefined or empty string won't be appended to the url.

  protected getUrl(path: QueryPath): string {
    let baseURL;
    if (this.serverType == ServerType.cwb) baseURL = AppSettings.cwbServer;
    else if (this.serverType == ServerType.dca) baseURL = AppSettings.DCAserver;
    else if (this.serverType == ServerType.dcaBackend) baseURL = AppSettings.DCABackendServer;
    else if (this.serverType == ServerType.dcaAdmin) {
      baseURL = AppSettings.DCAAdmin;
    } else if (this.serverType == ServerType.dcp) baseURL = AppSettings.DCPServer;
    else if (this.serverType == ServerType.tangra) baseURL = AppSettings.tangraURL;
    else if (this.serverType == ServerType.local)
      baseURL = 'http://localhost:5000'; // Temporary
    else if (this.serverType == ServerType.sap) baseURL = AppSettings.SAPBE;
    else if (this.serverType == ServerType.tangraDCA) baseURL = AppSettings.TangraDCAserver;
    else if (this.serverType == ServerType.prometheus) baseURL = AppSettings.prometheus;
    else if (this.serverType == ServerType.faultInjectionStatus)
      baseURL = AppSettings.faultInjectionStatus;

    let url: string = this.serviceType ? `${baseURL}/${this.serviceType}` : `${baseURL}`;

    if (path) {
      if (path.route && path.route.length > 0) {
        for (const route of path.route) {
          if (route && route !== 'undefined') {
            url += `/${route}`;
          }
        }
      }

      if (path.query) {
        let separator = '?';

        for (const name in path.query) {
          if (path.query[name]) {
            url += `${separator}${encodeURIComponent(name)}=${encodeURIComponent(
              path.query[name]!.toString(),
            )}`;
            separator = '&';
          }
        }
      }
    }
    return url;
  }

  protected prepareMultiPartForm(data: IMultipartFormData[]): FormData {
    //  It is expected that if a file is to be given a name that is different from the file system name,
    //  the interface created for such objects will have a key named "formDataName" and another named "file" (names are self-explanatory)

    const formData = new FormData();
    for (const item of data) {
      const { content, name } = item;
      if (Array.isArray(content) && content.length > 0) {
        for (const element of content) {
          if (
            typeof element === 'object' &&
            Object.prototype.hasOwnProperty.call(element, 'formDataName')
          ) {
            formData.append(name, element.file, element.formDataName);
          } else {
            formData.append(name, element);
          }
        }
      } else if (
        typeof content === 'object' &&
        content !== null &&
        Object.prototype.hasOwnProperty.call(content, 'formDataName')
      ) {
        formData.append(name, content.file, content.formDataName);
      } else {
        formData.append(name, content);
      }
    }
    return formData;
  }
}
